import React, { useEffect, useState } from 'react';
import { HeartIcon, WindIcon, BookOpenIcon, FootprintsIcon } from 'lucide-react';
type Trigger = 'bored' | 'lonely' | 'angry' | 'stressed' | 'tired' | 'other';
interface InterventionScreenProps {
  trigger: Trigger;
  onUrgeBeaten: () => void;
}
interface TriggerContent {
  dhikrArabic: string;
  dhikrTransliteration: string;
  dhikrMeaning: string;
  ayahText: string;
  ayahSource: string;
  actionIcon: React.ReactNode;
  actionText: string;
}
const TRIGGER_CONTENT: Record<Trigger, TriggerContent> = {
  bored: {
    dhikrArabic: 'سُبْحَانَ اللَّهِ وَبِحَمْدِهِ سُبْحَانَ اللَّهِ الْعَظِيمِ',
    dhikrTransliteration: 'SubhanAllahi wa bihamdihi, SubhanAllahil Azeem',
    dhikrMeaning: '"Glory be to Allah and His is the praise, Glory be to Allah the Supreme"',
    ayahText: '"Verily, with hardship comes ease."',
    ayahSource: 'Surah Ash-Sharh (94:6)',
    actionIcon: <FootprintsIcon className="w-6 h-6 text-primary" data-id="element-103" />,
    actionText: 'Go for a walk. Put on your shoes, step outside, and make dhikr as you walk. Movement breaks the cycle of boredom.'
  },
  lonely: {
    dhikrArabic: 'لَا إِلَٰهَ إِلَّا أَنتَ سُبْحَانَكَ إِنِّي كُنتُ مِنَ الظَّالِمِينَ',
    dhikrTransliteration: 'La ilaha illa Anta, Subhanaka inni kuntu minaz-zalimin',
    dhikrMeaning: '"There is no deity except You; exalted are You. Indeed, I have been of the wrongdoers."',
    ayahText: '"And He is with you wherever you are."',
    ayahSource: 'Surah Al-Hadid (57:4)',
    actionIcon: <HeartIcon className="w-6 h-6 text-primary" data-id="element-104" />,
    actionText: "Call someone you trust. A friend, a brother, family. Connection breaks isolation. You don't have to say why — just talk."
  },
  angry: {
    dhikrArabic: 'لَا حَوْلَ وَلَا قُوَّةَ إِلَّا بِاللَّهِ',
    dhikrTransliteration: 'La hawla wa la quwwata illa billah',
    dhikrMeaning: '"There is no power nor strength except with Allah"',
    ayahText: '"And those who restrain their anger and pardon people — Allah loves the doers of good."',
    ayahSource: "Surah Ali 'Imran (3:134)",
    actionIcon: <WindIcon className="w-6 h-6 text-primary" data-id="element-105" />,
    actionText: 'Make wudu. The cool water will calm your body and your nafs. The Prophet ﷺ said anger is from Shaitan, and Shaitan is created from fire, and fire is extinguished by water.'
  },
  stressed: {
    dhikrArabic: 'حَسْبُنَا اللَّهُ وَنِعْمَ الْوَكِيلُ',
    dhikrTransliteration: "HasbunAllahu wa ni'mal wakeel",
    dhikrMeaning: '"Allah is sufficient for us, and He is the best disposer of affairs"',
    ayahText: '"And whoever relies upon Allah — then He is sufficient for him."',
    ayahSource: 'Surah At-Talaq (65:3)',
    actionIcon: <BookOpenIcon className="w-6 h-6 text-primary" data-id="element-106" />,
    actionText: "Pray 2 rakkat. Stand before Allah and put this burden down. You don't have to carry it alone."
  },
  tired: {
    dhikrArabic: 'اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْهَمِّ وَالْحَزَنِ',
    dhikrTransliteration: "Allahumma inni a'udhu bika minal-hammi wal-hazan",
    dhikrMeaning: '"O Allah, I seek refuge in You from worry and grief"',
    ayahText: '"Allah does not burden a soul beyond that it can bear."',
    ayahSource: 'Surah Al-Baqarah (2:286)',
    actionIcon: <FootprintsIcon className="w-6 h-6 text-primary" data-id="element-107" />,
    actionText: 'Get up and change your environment. Move to a different room. Make wudu. The physical shift will help your mind shift too.'
  },
  other: {
    dhikrArabic: 'أَسْتَغْفِرُ اللَّهَ الْعَظِيمَ وَأَتُوبُ إِلَيْهِ',
    dhikrTransliteration: "Astaghfirullaha al-'Azeem wa atubu ilayh",
    dhikrMeaning: '"I seek forgiveness from Allah the Almighty and I repent to Him"',
    ayahText: '"Verily, with hardship comes ease."',
    ayahSource: 'Surah Ash-Sharh (94:6)',
    actionIcon: <FootprintsIcon className="w-6 h-6 text-primary" data-id="element-108" />,
    actionText: "Step away from your screen. Go somewhere public — a masjid, a park, a store. The urge will pass, insha'Allah."
  }
};
const TRIGGER_LABELS: Record<Trigger, string> = {
  bored: 'Boredom',
  lonely: 'Loneliness',
  angry: 'Anger',
  stressed: 'Stress',
  tired: 'Tiredness',
  other: 'This feeling'
};
export function InterventionScreen({
  trigger,
  onUrgeBeaten
}: InterventionScreenProps) {
  const content = TRIGGER_CONTENT[trigger];
  const label = TRIGGER_LABELS[trigger];
  const [breathPhase, setBreathPhase] = useState<'in' | 'out'>('in');
  useEffect(() => {
    const interval = setInterval(() => {
      setBreathPhase(prev => prev === 'in' ? 'out' : 'in');
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  return <main className="flex flex-col px-5 pt-8 pb-32 min-h-screen" data-id="element-109">
      {/* Context header */}
      <div className="text-center mb-6" data-id="element-110">
        <p className="text-sm text-muted-foreground mb-1" data-id="element-111">You're feeling</p>
        <h1 className="text-xl font-bold text-foreground" data-id="element-112">{label}</h1>
        <p className="text-sm text-muted-foreground mt-1" data-id="element-113">
          It's okay. Let's work through this together.
        </p>
      </div>

      {/* 1. Seek Refuge - Always first */}
      <section className="bg-card rounded-lg border border-border p-5 mb-4" aria-label="Seek refuge in Allah" data-id="element-114">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3" data-id="element-115">
          First, seek refuge
        </p>
        <p className="font-arabic text-2xl text-center leading-loose text-primary mb-2" dir="rtl" data-id="element-116">
          أَعُوذُ بِاللَّهِ مِنَ الشَّيْطَانِ الرَّجِيمِ
        </p>
        <p className="text-sm text-center text-muted-foreground italic mb-1" data-id="element-117">
          A'udhu Billahi Min ash-Shaytan ir-Rajim
        </p>
        <p className="text-sm text-center text-foreground" data-id="element-118">
          "I seek refuge in Allah from Satan, the accursed one"
        </p>
      </section>

      {/* 2. Trigger-specific dhikr */}
      <section className="bg-card rounded-lg border border-border p-5 mb-4" aria-label="Dhikr" data-id="element-119">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3" data-id="element-120">
          Dhikr for {label.toLowerCase()}
        </p>
        <p className="font-arabic text-2xl text-center leading-loose text-primary mb-2" dir="rtl" data-id="element-121">
          {content.dhikrArabic}
        </p>
        <p className="text-sm text-center text-muted-foreground italic mb-1" data-id="element-122">
          {content.dhikrTransliteration}
        </p>
        <p className="text-sm text-center text-foreground" data-id="element-123">
          {content.dhikrMeaning}
        </p>
        <p className="text-xs text-center text-muted-foreground mt-3" data-id="element-124">
          Repeat 3 times, slowly
        </p>
      </section>

      {/* 3. Breathing exercise */}
      <section className="bg-card rounded-lg border border-border p-6 mb-4 flex flex-col items-center" aria-label="Breathing exercise" data-id="element-125">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-5" data-id="element-126">
          Breathe
        </p>
        <div className="relative w-28 h-28 mb-4 flex items-center justify-center" data-id="element-127">
          <div className="absolute inset-0 rounded-full bg-primary/15 breathing-circle" data-id="element-128" />
          <div className="relative z-10 text-sm font-medium text-foreground" data-id="element-129">
            {breathPhase === 'in' ? 'Breathe in...' : 'Breathe out...'}
          </div>
        </div>
        <p className="text-xs text-muted-foreground text-center" data-id="element-130">
          Follow the circle. In through your nose, out through your mouth.
        </p>
      </section>

      {/* 4. Relevant ayah */}
      <section className="bg-card rounded-lg border border-border p-5 mb-4" aria-label="Quranic verse" data-id="element-131">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3" data-id="element-132">
          From the Quran
        </p>
        <blockquote className="text-base text-foreground leading-relaxed text-center mb-2 font-medium" data-id="element-133">
          {content.ayahText}
        </blockquote>
        <p className="text-sm text-muted-foreground text-center" data-id="element-134">
          — {content.ayahSource}
        </p>
      </section>

      {/* 5. Action suggestion */}
      <section className="bg-card rounded-lg border border-primary/30 p-5 mb-8" aria-label="Action suggestion" data-id="element-135">
        <div className="flex items-start gap-3" data-id="element-136">
          <div className="w-10 h-10 rounded-full bg-highlight/40 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-137">
            {content.actionIcon}
          </div>
          <div data-id="element-138">
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2" data-id="element-139">
              What to do right now
            </p>
            <p className="text-base text-foreground leading-relaxed" data-id="element-140">
              {content.actionText}
            </p>
          </div>
        </div>
      </section>

      {/* 6. "I beat this urge" button */}
      <button onClick={onUrgeBeaten} className="w-full bg-success text-success-foreground font-semibold text-lg rounded-lg py-4 px-6 min-h-[56px] shadow-md hover:shadow-lg active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2" aria-label="I beat this urge, Alhamdulillah" data-id="element-141">
        <HeartIcon className="w-5 h-5" data-id="element-142" />
        Alhamdulillah, I beat this urge
      </button>
    </main>;
}